import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import CallbackPopup from '../components/CallbackPopup';
import '../styles/blog-content.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [caseStudy, setCaseStudy] = useState(null);
  const [relatedCaseStudies, setRelatedCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCallbackPopupOpen, setIsCallbackPopupOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchCaseStudy();
    }
  }, [slug]);

  const fetchCaseStudy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/case-studies/slug/${slug}`);
      
      if (response.status === 404) {
        setError('Case study not found');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch case study');
      }

      const data = await response.json();
      setCaseStudy(data);
      
      // Fetch related case studies after we have the current case study data
      fetchRelatedCaseStudies(data._id);
    } catch (error) {
      console.error('Error fetching case study:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedCaseStudies = async (currentCaseStudyId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/case-studies?limit=6&status=published`);
      if (response.ok) {
        const data = await response.json();
        // Filter out the current case study and limit to 4 results
        const filteredCaseStudies = (data.caseStudies || [])
          .filter(caseStudy => caseStudy._id !== currentCaseStudyId)
          .slice(0, 4);
        setRelatedCaseStudies(filteredCaseStudies);
      }
    } catch (error) {
      console.error('Error fetching related case studies:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading case study...</p>
          </div>
        </main>
        <Footer />
        <FloatingChatButton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-red-600 mb-4">Case Study Not Found</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={() => navigate('/knowledge-center')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              ← Back to Knowledge Center
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="container mx-auto max-w-7xl text-center">
            <div className="mb-6">
              {caseStudy.category && (
                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {caseStudy.category}
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {caseStudy.title}
            </h1>
            <div className="flex items-center justify-center space-x-6 text-gray-600">
              <div className="flex items-center">
                <span className="font-medium">By {caseStudy.author || 'Medical Linkers Team'}</span>
              </div>
              {caseStudy.createdAt && (
                <div className="flex items-center">
                  <span>{formatDate(caseStudy.publishedAt || caseStudy.createdAt)}</span>
                </div>
              )}
              {caseStudy.readTime && (
                <div className="flex items-center">
                  <span>{caseStudy.readTime} min read</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="py-8">
          <div className="container mx-auto max-w-7xl px-6">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
              {caseStudy.featuredImage ? (
                <img 
                  src={caseStudy.featuredImage} 
                  alt={caseStudy.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://dummyimage.com/800x600/eeeeee";
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500">No Image Available</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Case Study Content */}
        <section className="py-8">
          <div className="container mx-auto max-w-4xl px-6">
            {/* Main Content */}
            <div className="w-full">
              <article className="w-full">
                {/* Description */}
                {caseStudy.description && (
                  <div className="text-xl text-gray-700 leading-relaxed mb-8 p-8 bg-blue-50 rounded-lg border-l-4 border-blue-500 w-full">
                    {caseStudy.description}
                  </div>
                )}

                {/* Full Content */}
                <div 
                  className="blog-content w-full"
                  style={{
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal'
                  }}
                  dangerouslySetInnerHTML={{ __html: caseStudy.content }}
                />

                {/* Tags */}
                {caseStudy.tags && caseStudy.tags.length > 0 && (
                  <div className="mt-12 pt-8 border-t border-gray-200 w-full">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Tags</h3>
                    <div className="flex flex-wrap gap-3">
                      {caseStudy.tags.map((tag, index) => (
                        <span 
                          key={index}
                          className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors cursor-pointer"
                        >
                          #{tag.replace(/[\[\]'"]/g, '')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share Section */}
                <div className="mt-12 pt-8 border-t border-gray-200 w-full">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Share this case study</h3>
                  <div className="flex space-x-4">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(caseStudy.title)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-400 text-white px-6 py-3 rounded-lg hover:bg-blue-500 transition-colors font-medium"
                    >
                      Twitter
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Facebook
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-700 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors font-medium"
                    >
                      LinkedIn
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Author and Related Content Section */}
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl px-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Author Info */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">About the Author</h3>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {(caseStudy.author || 'Medical Linkers Team').charAt(0)}
                  </div>
                  <div className="ml-4">
                    <p className="font-medium text-gray-900">{caseStudy.author || 'Medical Linkers Team'}</p>
                    <p className="text-sm text-gray-600">Medical Content Writer</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-blue-600 rounded-lg p-6 text-white">
                <h3 className="text-lg font-semibold mb-2">Need Medical Consultation?</h3>
                <p className="text-blue-100 text-sm mb-4">
                  Get expert medical advice from our specialists based on this case study.
                </p>
                <button 
                  onClick={() => setIsCallbackPopupOpen(true)}
                  className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors font-medium w-full"
                >
                  Contact Us
                </button>
              </div>
            </div>

            {/* Related Case Studies */}
            {relatedCaseStudies.length > 0 && (
              <div className="mt-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Related Case Studies</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedCaseStudies.slice(0, 3).map((relatedCaseStudy) => (
                    <div key={relatedCaseStudy._id} className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      <a 
                        href={`/case-studies/${relatedCaseStudy.slug}`}
                        className="block"
                      >
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={relatedCaseStudy.featuredImage}
                            alt={relatedCaseStudy.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = "https://dummyimage.com/300x200/eeeeee";
                            }}
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors leading-tight mb-2">
                            {relatedCaseStudy.title}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {formatDate(relatedCaseStudy.publishedAt || relatedCaseStudy.createdAt)}
                          </p>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Navigation */}
        <section className="py-8 border-t border-gray-200">
          <div className="container mx-auto max-w-7xl px-6">
            <div className="flex justify-between items-center">
              <button
                onClick={() => navigate('/case-studies')}
                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Back to Case Studies
              </button>
              
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Back to Top ↑
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingChatButton />
      
      {/* Callback Popup */}
      <CallbackPopup 
        isOpen={isCallbackPopupOpen} 
        onClose={() => setIsCallbackPopupOpen(false)} 
      />
    </div>
  );
};

export default CaseStudyDetail;
